import React, { useState, useEffect } from "react";

import { useDispatch, useSelector } from "react-redux";

import { Container, Card, Spinner, Row, Col, Form } from "react-bootstrap";

import { Formik } from "formik";
import * as Yup from "yup";

import { useTranslation } from "react-i18next";

import { showAlert } from "store/reducers/alertReducer";

import { initiateTapOnboarding } from "store/actions/tapOnboardingSlice";

import { getCompanyInfo } from "store/actions/authSlice";

import CommonTitleBar from "components/common/CommonTitleBar";
import CustomField from "components/common/CustomField";
import CustomLabel from "components/common/CustomLabel";

const TITLE_OPTIONS = ["Mr.", "Ms.", "Mrs.", "Dr."];

const NAME_REGEX = /^[a-zA-Z؀-ۿ\s]+$/;
const NAME_REGEX_OPTIONAL = /^[a-zA-Z؀-ۿ\s]*$/;

const leadDetailsSchema = Yup.object().shape({
  title: Yup.string()
    .oneOf(["", ...TITLE_OPTIONS], "Select a valid title")
    .optional(),

  firstName: Yup.string()
    .required("First name is required")
    .min(2, "At least 2 characters")
    .max(50, "Max 50 characters")
    .matches(NAME_REGEX, "Letters only, no numbers or symbols")
    .test("no-spaces", "Cannot be only spaces", (v) => v?.trim().length > 0),

  middleName: Yup.string()
    .max(50, "Max 50 characters")
    .matches(NAME_REGEX_OPTIONAL, "Letters only, no numbers or symbols"),

  lastName: Yup.string()
    .max(50, "Max 50 characters")
    .matches(NAME_REGEX_OPTIONAL, "Letters only, no numbers or symbols"),

  email: Yup.string()
    .required("Email is required")
    .email("Enter a valid email address")
    .max(100, "Max 100 characters"),

  phoneCode: Yup.string()
    .required("Phone code is required")
    .matches(/^\d{1,4}$/, "Digits only, 1–4 digits (e.g. 966)"),

  phoneNumber: Yup.string()
    .required("Phone number is required")
    .matches(/^\d{7,15}$/, "Must be 7–15 digits"),

  brandName: Yup.string()
    .required("Brand name is required")
    .min(2, "At least 2 characters")
    .max(100, "Max 100 characters")
    .test("no-spaces", "Cannot be only spaces", (v) => v?.trim().length > 0),

  country: Yup.string()
    .matches(/^[A-Za-z]{2}$/, "Must be a 2-letter country code (e.g. SA)")
    .optional(),
});

function TapOnboarding() {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const { organizationManagerDetails, orgnisationDetails } = useSelector(
    (state) => state.auth,
  );

  const [pageLoading, setPageLoading] = useState(true);
  const [btnLoading, setBtnLoading] = useState(false);

  const tap = organizationManagerDetails?.merchantInfo?.tapInfo || {};
  const status = tap?.onboardingStatus || "not_started";

  const statusConfig = {
    not_started: {
      label: t("Not Started"),
      badgeClass: "badge bg-secondary",
    },
    connect_created: {
      label: t("In Progress"),
      badgeClass: "badge bg-warning text-dark",
    },
    merchant_active: {
      label: t("Active"),
      badgeClass: "badge bg-success",
    },
    merchant_rejected: {
      label: t("Rejected"),
      badgeClass: "badge bg-danger",
    },
  };

  const statusGuidance = {
    not_started: t(
      "Fill in your details below and click 'Start Onboarding' to connect with Tap Payments.",
    ),
    connect_created: t(
      "Your onboarding is in progress. Click 'Continue' to complete the setup on the Tap portal.",
    ),
    merchant_active: t(
      "Your account is connected and active. You can now accept payments through Tap.",
    ),
    merchant_rejected: t(
      "Your onboarding was rejected. Update your details below and click 'Retry' to try again.",
    ),
  };

  const showForm = status === "not_started" || status === "merchant_rejected";

  useEffect(() => {
    fetchCompanyInfo();
  }, []);

  const fetchCompanyInfo = async () => {
    try {
      setPageLoading(true);
      await dispatch(getCompanyInfo());
    } catch (error) {
      dispatch(
        showAlert({
          type: "error",
          text: t("Failed to load company info"),
        }),
      );
    } finally {
      setPageLoading(false);
    }
  };

  const getInitialValues = () => ({
    title: "Mr",
    firstName: orgnisationDetails?.name || "",
    middleName: "",
    lastName: "",
    email: orgnisationDetails?.email || "",
    phoneCode: orgnisationDetails?.phoneCode?.replace("+", "") || "",
    phoneNumber: orgnisationDetails?.phoneNumber || "",
    brandName: orgnisationDetails?.companyDetails?.companyName || "",
    country: "SA",
  });

  const handleStartOnboarding = async (leadDetails) => {
    try {
      setBtnLoading(true);

      const response = await dispatch(initiateTapOnboarding({ leadDetails }));
      const result = response?.payload;

      if (result?.success) {
        const connectUrl = result?.data?.connectUrl;
        await dispatch(getCompanyInfo());
        window.location.href = connectUrl;
      } else {
        dispatch(
          showAlert({
            type: "error",
            text: result?.message || t("Failed to start onboarding"),
          }),
        );
      }
    } catch (error) {
      dispatch(
        showAlert({
          type: "error",
          text: t("Something went wrong. Please try again."),
        }),
      );
    } finally {
      setBtnLoading(false);
    }
  };

  const handleResumeOnboarding = () => {
    if (tap?.connectUrl) {
      window.location.href = tap.connectUrl;
    }
  };

  if (pageLoading) {
    return (
      <div className="text-center mt-5">
        <Spinner animation="border" />
      </div>
    );
  }

  return (
    <Container>
      <CommonTitleBar title={t("Merchant Onboarding")} />

      <Card className="p-4 shadow-sm mt-3">
        <h5 className="mb-1">{t("Tap Payment Setup")}</h5>
        <p className="text-muted mb-3">
          {t("Manage your Tap Payments merchant connection.")}
        </p>

        <hr />

        {/* Status row */}
        <div className="d-flex align-items-center gap-2 mb-1">
          <span className="text-muted">{t("Status")}:</span>
          <span
            className={statusConfig[status]?.badgeClass || "badge bg-secondary"}
          >
            {statusConfig[status]?.label || status}
          </span>
        </div>
        <p className="text-muted small mb-3">{statusGuidance[status]}</p>

        {tap?.merchantId && (
          <p className="mb-3">
            <span className="text-muted">{t("Merchant ID")}:</span>
            <b className="ms-2">{tap.merchantId}</b>
          </p>
        )}

        {/* Lead details form — only for actionable states */}
        {showForm && (
          <Formik
            initialValues={getInitialValues()}
            validationSchema={leadDetailsSchema}
            onSubmit={(values) => handleStartOnboarding(values)}
            enableReinitialize
          >
            {({ handleSubmit, touched, errors, values, setFieldValue }) => (
              <Form onSubmit={handleSubmit}>
                <Row className="mb-3">
                  <Col md={6}>
                    <CustomLabel htmlFor="brandName" label={t("Brand Name")} />
                    <CustomField
                      name="brandName"
                      placeholder={t("Brand Name")}
                      touched={touched}
                      errors={errors}
                    />
                  </Col>
                  <Col md={6}>
                    <CustomLabel htmlFor="country" label={t("Country Code")} />
                    <CustomField
                      name="country"
                      placeholder={t("e.g. SA")}
                      touched={touched}
                      errors={errors}
                    />
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col md={3}>
                    <CustomLabel htmlFor="title" label={t("Title")} />
                    <Form.Select
                      id="title"
                      value={values.title}
                      onChange={(e) => setFieldValue("title", e.target.value)}
                    >
                      <option value="">{t("Select")}</option>
                      {TITLE_OPTIONS.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </Form.Select>
                  </Col>
                  <Col md={3}>
                    <CustomLabel htmlFor="firstName" label={t("First Name")} />
                    <CustomField
                      name="firstName"
                      placeholder={t("First Name")}
                      touched={touched}
                      errors={errors}
                    />
                  </Col>
                  <Col md={3}>
                    <CustomLabel
                      htmlFor="middleName"
                      label={t("Middle Name")}
                    />
                    <CustomField
                      name="middleName"
                      placeholder={t("Middle Name")}
                      touched={touched}
                      errors={errors}
                    />
                  </Col>
                  <Col md={3}>
                    <CustomLabel htmlFor="lastName" label={t("Last Name")} />
                    <CustomField
                      name="lastName"
                      placeholder={t("Last Name")}
                      touched={touched}
                      errors={errors}
                    />
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col md={6}>
                    <CustomLabel htmlFor="email" label={t("Email")} />
                    <CustomField
                      name="email"
                      type="email"
                      placeholder={t("Email")}
                      touched={touched}
                      errors={errors}
                    />
                  </Col>
                  <Col md={2}>
                    <CustomLabel htmlFor="phoneCode" label={t("Phone Code")} />
                    <CustomField
                      name="phoneCode"
                      placeholder={t("e.g. 966")}
                      touched={touched}
                      errors={errors}
                    />
                  </Col>
                  <Col md={4}>
                    <CustomLabel
                      htmlFor="phoneNumber"
                      label={t("Phone Number")}
                    />
                    <CustomField
                      name="phoneNumber"
                      placeholder={t("Phone Number")}
                      touched={touched}
                      errors={errors}
                    />
                  </Col>
                </Row>

                <div className="d-flex justify-content-end mt-2">
                  <button
                    type="submit"
                    className="main_btn"
                    disabled={btnLoading}
                  >
                    {btnLoading ? (
                      <Spinner size="sm" />
                    ) : status === "merchant_rejected" ? (
                      t("Retry Onboarding")
                    ) : (
                      t("Start Onboarding")
                    )}
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        )}

        {/* Continue button for in-progress state */}
        {status === "connect_created" && (
          <div className="d-flex justify-content-end">
            <button className="main_btn" onClick={handleResumeOnboarding}>
              {t("Continue Onboarding")}
            </button>
          </div>
        )}

        {/* Success panel */}
        {status === "merchant_active" && (
          <div className="mt-3 p-3 rounded border border-success bg-success bg-opacity-10">
            <div className="d-flex align-items-center gap-2">
              <span className="badge bg-success fs-6">&#10003;</span>
              <strong className="text-success">
                {t("Tap Connected Successfully")}
              </strong>
            </div>
            <p className="text-muted small mt-2 mb-0">
              {t(
                "Your merchant account is active. Payments made by customers will be processed through Tap.",
              )}
            </p>
          </div>
        )}
      </Card>
    </Container>
  );
}

export default TapOnboarding;
